// @ts-nocheck
/**
 * 商人ギルド画面
 *
 * 商人一覧を表示し、各商人の店舗へ遷移する
 */

import { economyAccount, economyRequest } from '../../lib/economyClient';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  MERCHANTS,
  MERCHANT_TYPES,
  TICKETS,
  getTodayAppearances,
  callAttributeMerchant,
  ATTRIBUTE_MERCHANTS,
  countForbiddenCards,
} from '../merchant';
import { calculateTotalAssets } from '../systems/assetCalculator';
import { useIsMobile } from '../hooks/useMediaQuery';
import TournamentTab from '../tournament/components/TournamentTab';
import InformationBureau from '../investment/components/InformationBureau';
import TradeScreen from './TradeScreen';
import { currencyManager } from '../systems/currencyManager';
import { packSystem } from '../systems/packSystem';
import { packSystemServer } from '../systems/packSystemServer';
import { useAuth } from '../../lib/useAuth';
import { ECONOMY, ATTRIBUTE_PACK, ROYAL_PACK } from '../data/constants';
import { DAYS_PER_WEEK } from '../market/constants';
import {
  getMarketIndexChartData,
  getTrendIcon,
  getTrendColor,
  generateSparklineData,
} from '../market/priceHistory';
import { buildMarketIndex } from '../market/marketIndex';

// サーバーAPI使用フラグ（環境変数またはlocalStorageで切り替え可能）
const USE_SERVER_API = (() => {
  try {
    // localStorageで明示的に無効化されていない限りサーバーAPIを使用
    return localStorage.getItem('magisp_use_local_pack') !== 'true';
  } catch {
    return true;
  }
})();

// ========================================
// スタイル定義
// ========================================

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 50%, #0a0a1a 100%)',
    fontFamily: '"Noto Sans JP", "Hiragino Sans", sans-serif',
    color: '#e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
  },
  // モバイル用コンテナ（固定ヘッダー/フッター対応）
  containerMobile: {
    minHeight: 'calc(100vh - 126px)',  // ヘッダー・フッター分を引いた高さ
    background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 50%, #0a0a1a 100%)',
    fontFamily: '"Noto Sans JP", "Hiragino Sans", sans-serif',
    color: '#e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    paddingTop: '56px', // ヘッダー高さ分
    paddingBottom: '70px', // フッター高さ分
  },
  header: {
    background: 'linear-gradient(90deg, rgba(20,20,50,0.95) 0%, rgba(40,20,60,0.95) 50%, rgba(20,20,50,0.95) 100%)',
    borderBottom: '2px solid #6b4ce6',
    padding: '12px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 4px 20px rgba(107,76,230,0.3)',
  },
  // モバイル用固定ヘッダー
  headerMobile: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(90deg, rgba(20,20,50,0.98) 0%, rgba(40,20,60,0.98) 50%, rgba(20,20,50,0.98) 100%)',
    borderBottom: '2px solid #6b4ce6',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 4px 20px rgba(107,76,230,0.3)',
    zIndex: 100,
  },
  // フッタータブナビゲーション（モバイル用）
  footerTabNavigation: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-around',
    padding: '6px 4px',
    paddingBottom: 'calc(6px + env(safe-area-inset-bottom))',
    background: 'linear-gradient(180deg, rgba(20,20,50,0.98) 0%, rgba(10,10,30,0.99) 100%)',
    borderTop: '2px solid #6b4ce6',
    boxShadow: '0 -4px 20px rgba(107,76,230,0.3)',
    zIndex: 100,
  },
  footerTab: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 2px',
    border: 'none',
    background: 'transparent',
    color: '#808090',
    fontSize: '10px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    borderRadius: '8px',
    margin: '0 2px',
    // タッチ操作の改善
    touchAction: 'manipulation', // ダブルタップズームを無効化
    WebkitTapHighlightColor: 'transparent', // タップハイライトを無効化
    userSelect: 'none', // テキスト選択を無効化
  },
  footerTabActive: {
    color: '#fff',
    background: 'linear-gradient(180deg, rgba(107,76,230,0.4) 0%, rgba(107,76,230,0.2) 100%)',
  },
  footerTabIcon: {
    fontSize: '20px',
    marginBottom: '2px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'rgba(107,76,230,0.3)',
    color: '#e0e0e0',
    transition: 'all 0.3s ease',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    background: 'linear-gradient(90deg, #ffd700, #ff9500, #ffd700)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  goldDisplay: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#ffd700',
  },
  mainContent: {
    flex: 1,
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#e0e0e0',
    marginBottom: '16px',
    paddingLeft: '8px',
    borderLeft: '4px solid #6b4ce6',
  },
  merchantGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  merchantCard: {
    background: 'linear-gradient(135deg, #2a2a4a 0%, #3a3a5a 100%)',
    borderRadius: '12px',
    padding: '16px',
    border: '2px solid #4a4a6a',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  merchantCardHover: {
    border: '2px solid #6b4ce6',
    boxShadow: '0 0 20px rgba(107,76,230,0.4)',
    transform: 'translateY(-2px)',
  },
  merchantCardDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  merchantIcon: {
    fontSize: '36px',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(107,76,230,0.2)',
    borderRadius: '50%',
  },
  merchantInfo: {
    flex: 1,
  },
  merchantName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: '4px',
  },
  merchantSpecialty: {
    fontSize: '12px',
    color: '#a0a0a0',
  },
  merchantFav: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#ff69b4',
  },
  wishlistBadge: {
    background: 'linear-gradient(90deg, #ffd700, #ff9500)',
    color: '#1a1a2e',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 'bold',
  },
  enterButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'linear-gradient(90deg, #6b4ce6, #9d4ce6)',
    color: '#fff',
    transition: 'all 0.3s ease',
  },
  ticketSection: {
    background: 'rgba(107,76,230,0.1)',
    borderRadius: '12px',
    padding: '16px',
    marginTop: '24px',
    border: '1px solid #4a4a6a',
  },
  ticketGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '12px',
  },
  ticketItem: {
    background: 'rgba(50,50,80,0.5)',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  ticketCount: {
    fontWeight: 'bold',
    color: '#ffd700',
  },
  noticeCard: {
    background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,150,0,0.1) 100%)',
    border: '1px solid #ffd700',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  noticeIcon: {
    fontSize: '24px',
  },
  noticeText: {
    flex: 1,
    fontSize: '14px',
    color: '#ffd700',
  },
  ticketUseButton: {
    padding: '4px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '12px',
    background: 'linear-gradient(90deg, #ff9800, #ffc107)',
    color: '#1a1a2e',
    marginLeft: '8px',
    transition: 'all 0.3s ease',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: 'linear-gradient(135deg, #2a2a4a 0%, #3a3a5a 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '2px solid #6b4ce6',
    maxWidth: '400px',
    width: '90%',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: '16px',
    textAlign: 'center',
  },
  modalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  modalItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '8px',
    background: 'rgba(107,76,230,0.2)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  modalItemDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  modalCancelButton: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'rgba(100,100,100,0.5)',
    color: '#fff',
    transition: 'all 0.3s ease',
  },
  // タブナビゲーション
  tabNavigation: {
    display: 'flex',
    gap: '4px',
    padding: '8px 24px',
    background: 'rgba(20,20,50,0.5)',
    borderBottom: '1px solid #4a4a6a',
  },
  tab: {
    padding: '10px 24px',
    borderRadius: '8px 8px 0 0',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'rgba(60,60,100,0.3)',
    color: '#a0a0a0',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tabActive: {
    background: 'linear-gradient(90deg, #6b4ce6, #9d4ce6)',
    color: '#fff',
    boxShadow: '0 0 10px rgba(107,76,230,0.4)',
  },
  // パックタブ用スタイル
  packTabContent: {
    flex: 1,
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  packLeftColumn: {
    flex: '1 1 400px',
    maxWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  packRightColumn: {
    flex: '1 1 350px',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  packCard: {
    background: 'linear-gradient(135deg, #2a2a4a 0%, #3a3a5a 50%, #2a2a4a 100%)',
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    border: '2px solid #6b4ce6',
    boxShadow: '0 0 30px rgba(107,76,230,0.3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    boxSizing: 'border-box',
  },
  packName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
  },
  packImage: {
    width: '120px',
    height: '160px',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    boxShadow: '0 4px 15px rgba(107,76,230,0.5)',
  },
  packInfo: {
    fontSize: '14px',
    color: '#a0a0a0',
    textAlign: 'center',
  },
  packPrice: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ffd700',
  },
  buyButton: {
    padding: '12px 32px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    color: '#fff',
    transition: 'all 0.3s ease',
    width: '100%',
  },
  buyButtonDisabled: {
    background: 'rgba(100,100,100,0.5)',
    cursor: 'not-allowed',
  },
  packTypeTabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
    width: '100%',
  },
  packTypeTab: {
    flex: 1,
    padding: '10px 8px',
    borderRadius: '8px 8px 0 0',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    background: 'rgba(107,76,230,0.2)',
    color: '#a0a0a0',
    transition: 'all 0.2s ease',
  },
  packTypeTabActive: {
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    color: '#fff',
  },
  attributeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
    marginBottom: '16px',
    width: '100%',
  },
  attributeButton: {
    padding: '12px 8px',
    borderRadius: '8px',
    border: '2px solid rgba(107,76,230,0.3)',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    background: 'rgba(20,20,40,0.8)',
    color: '#e0e0e0',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  attributeButtonSelected: {
    borderColor: '#ffd700',
    background: 'rgba(255,215,0,0.15)',
    boxShadow: '0 0 10px rgba(255,215,0,0.3)',
  },
  bulkBuySection: {
    marginTop: '16px',
    padding: '16px',
    background: 'rgba(107,76,230,0.1)',
    borderRadius: '12px',
    border: '1px dashed rgba(107,76,230,0.4)',
    width: '100%',
    boxSizing: 'border-box',
  },
  bulkBuyTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#b0a0e0',
    marginBottom: '12px',
    textAlign: 'center',
  },
  bulkBuyButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  bulkBuyButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid rgba(107,76,230,0.5)',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    background: 'rgba(107,76,230,0.2)',
    color: '#e0e0e0',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    minWidth: '90px',
  },
  bulkBuyButtonDisabled: {
    background: 'rgba(50,50,50,0.5)',
    borderColor: 'rgba(100,100,100,0.3)',
    color: '#606060',
    cursor: 'not-allowed',
  },
  unopenedPackCard: {
    background: 'linear-gradient(135deg, #3a2a1a 0%, #5a4a2a 50%, #3a2a1a 100%)',
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    border: '2px solid #ffd700',
    boxShadow: '0 0 30px rgba(255,215,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    boxSizing: 'border-box',
  },
  openPackButton: {
    padding: '12px 32px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
    background: 'linear-gradient(135deg, #ff9500 0%, #ffd700 100%)',
    color: '#1a1a2e',
    transition: 'all 0.3s ease',
    width: '100%',
  },
  royalPackCard: {
    background: 'linear-gradient(135deg, #3a2a1a 0%, #5a4a2a 50%, #3a2a1a 100%)',
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    border: '3px solid #ffd700',
    boxShadow: '0 0 40px rgba(255,215,0,0.4)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    boxSizing: 'border-box',
  },
  royalPackImage: {
    width: '120px',
    height: '160px',
    background: 'linear-gradient(135deg, #ffd700 0%, #ff9500 50%, #ffd700 100%)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    boxShadow: '0 4px 20px rgba(255,215,0,0.5)',
  },
  // マーケットニュース用スタイル
  marketNewsPanel: {
    width: '100%',
    background: 'linear-gradient(135deg, rgba(20,30,50,0.9) 0%, rgba(30,40,60,0.9) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(107,156,230,0.5)',
    padding: '16px',
    boxShadow: '0 4px 20px rgba(107,156,230,0.2)',
    boxSizing: 'border-box',
  },
  msiSection: {
    width: '100%',
    background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,165,0,0.08) 100%)',
    borderRadius: '12px',
    border: '2px solid rgba(255,215,0,0.4)',
    padding: '16px',
    boxShadow: '0 4px 20px rgba(255,215,0,0.15)',
    boxSizing: 'border-box',
  },
  // 市場タブ用追加スタイル
  marketTabContent: {
    flex: 1,
    padding: '24px',
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  marketNewsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(107,156,230,0.3)',
  },
  marketNewsIcon: { fontSize: '20px' },
  marketNewsTitle: { fontSize: '16px', fontWeight: 'bold', color: '#9dc4ff' },
  marketDayCounter: { marginLeft: 'auto', fontSize: '12px', color: '#808080' },
  trendSection: {
    marginBottom: '12px',
    padding: '10px',
    background: 'rgba(107,156,230,0.1)',
    borderRadius: '8px',
  },
  trendLabel: { fontSize: '12px', color: '#6ba0e0', marginBottom: '4px' },
  trendName: { fontSize: '14px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '4px' },
  trendEffects: { fontSize: '12px', color: '#a0a0a0' },
  trendEffectUp: { color: '#6bff6b' },
  trendEffectDown: { color: '#ff6b6b' },
  newsSection: {
    padding: '10px',
    background: 'rgba(255,200,100,0.1)',
    borderRadius: '8px',
    borderLeft: '3px solid #ffc864',
  },
  newsLabel: { fontSize: '12px', color: '#c8a050', marginBottom: '4px' },
  newsText: { fontSize: '13px', color: '#e0e0e0', lineHeight: '1.5' },
  newsModifier: { marginTop: '6px', fontSize: '12px', fontWeight: 'bold' },
  modifierUp: { color: '#6bff6b' },
  modifierDown: { color: '#ff6b6b' },
  weekProgress: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '11px',
    color: '#808080',
  },
  weekProgressBar: {
    flex: 1,
    height: '4px',
    background: 'rgba(107,156,230,0.2)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  weekProgressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #6ba0e0, #9dc4ff)',
    borderRadius: '2px',
  },
  chainSection: {
    marginTop: '8px',
    padding: '10px',
    background: 'rgba(100,180,255,0.08)',
    borderRadius: '8px',
    borderLeft: '3px solid #64b4ff',
  },
  chainLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#64b4ff',
    marginBottom: '8px',
    fontWeight: 'bold',
  },
  chainItem: {
    padding: '6px 8px',
    marginBottom: '6px',
    background: 'rgba(100,180,255,0.1)',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#e0e0e0',
    lineHeight: '1.4',
  },
  chainItemLast: { marginBottom: 0 },
  chainType: { fontSize: '11px', fontWeight: 'bold', marginRight: '6px' },
  chainTypePositive: { color: '#6bff6b' },
  chainTypeCounter: { color: '#ffaa00' },
  chainTypeNegative: { color: '#ff6b6b' },
  chainTypeSpecial: { color: '#ff64ff' },
  chainModifier: { marginTop: '4px', fontSize: '11px', fontWeight: 'bold' },
  subNewsSection: {
    marginTop: '8px',
    padding: '10px',
    background: 'rgba(150,120,255,0.08)',
    borderRadius: '8px',
    borderLeft: '3px solid #9678ff',
  },
  subNewsLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#9678ff',
    marginBottom: '6px',
    fontWeight: 'bold',
  },
  subNewsText: { fontSize: '12px', color: '#e0e0e0', lineHeight: '1.4' },
  subNewsModifier: { marginTop: '4px', fontSize: '11px', fontWeight: 'bold' },
  suddenEventSection: {
    marginTop: '12px',
    padding: '12px',
    background: 'linear-gradient(135deg, rgba(255,50,50,0.15) 0%, rgba(255,100,50,0.1) 100%)',
    borderRadius: '8px',
    border: '1px solid rgba(255,100,100,0.4)',
    borderLeft: '3px solid #ff6b6b',
  },
  suddenEventLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#ff8080',
    marginBottom: '6px',
    fontWeight: 'bold',
  },
  suddenEventName: { fontSize: '14px', fontWeight: 'bold', color: '#ffa0a0', marginBottom: '4px' },
  suddenEventEffects: { fontSize: '12px', color: '#e0e0e0', lineHeight: '1.6' },
  suddenEventEffect: { marginBottom: '2px' },
  spotlightSection: {
    marginTop: '12px',
    padding: '12px',
    background: 'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(255,165,0,0.15) 100%)',
    borderRadius: '8px',
    border: '2px solid rgba(255,215,0,0.6)',
    borderLeft: '4px solid #ffd700',
    boxShadow: '0 0 15px rgba(255,215,0,0.3)',
  },
  spotlightLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#ffd700',
    marginBottom: '8px',
    fontWeight: 'bold',
  },
  spotlightTitle: { fontSize: '15px', fontWeight: 'bold', color: '#fff', marginBottom: '6px' },
  spotlightCardName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#ffd700',
    marginBottom: '4px',
    textShadow: '0 0 8px rgba(255,215,0,0.5)',
  },
  spotlightDescription: { fontSize: '13px', color: '#e0e0e0', lineHeight: '1.5' },
  spotlightBonus: {
    marginTop: '8px',
    padding: '6px 10px',
    background: 'rgba(255,215,0,0.2)',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#6bff6b',
    textAlign: 'center',
  },
  spotlightHint: { marginTop: '6px', fontSize: '11px', color: '#b0a060', textAlign: 'center' },
  overnightSection: {
    marginTop: '12px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid',
  },
  overnightSectionUp: {
    background: 'linear-gradient(135deg, rgba(76,175,80,0.15) 0%, rgba(76,175,80,0.1) 100%)',
    borderColor: 'rgba(76,175,80,0.4)',
    borderLeft: '3px solid #4caf50',
  },
  overnightSectionDown: {
    background: 'linear-gradient(135deg, rgba(244,67,54,0.15) 0%, rgba(244,67,54,0.1) 100%)',
    borderColor: 'rgba(244,67,54,0.4)',
    borderLeft: '3px solid #f44336',
  },
  overnightLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    marginBottom: '6px',
    fontWeight: 'bold',
  },
  overnightText: { fontSize: '13px', color: '#e0e0e0', lineHeight: '1.5' },
  overnightPercent: { marginTop: '6px', fontSize: '14px', fontWeight: 'bold', textAlign: 'right' },
  msiHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  msiTitle: { display: 'flex', alignItems: 'center', gap: '8px' },
  msiIcon: { fontSize: '24px' },
  msiLabel: { fontSize: '14px', fontWeight: 'bold', color: '#ffd700' },
  msiDetailButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255,215,0,0.5)',
    background: 'rgba(255,215,0,0.1)',
    color: '#ffd700',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },
  msiBody: { display: 'flex', alignItems: 'center', gap: '16px' },
  msiValue: { fontSize: '32px', fontWeight: 'bold', color: '#ffd700' },
  msiChange: { display: 'flex', flexDirection: 'column', gap: '4px' },
  msiChangeValue: { fontSize: '16px', fontWeight: 'bold' },
  msiCondition: { fontSize: '12px', padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold' },
  msiSparkline: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    height: '40px',
    gap: '2px',
    marginLeft: 'auto',
  },
  msiSparklineBar: {
    width: '6px',
    backgroundColor: '#ffd700',
    borderRadius: '2px 2px 0 0',
    transition: 'height 0.3s',
    opacity: 0.7,
  },
};

// ========================================
// 属性アイコンマッピング
// ========================================

const ATTRIBUTE_ICONS = {
  '炎': '🔥',
  '水': '💧',
  '光': '✨',
  '闇': '🌙',
  '原始': '🌿',
  '未来': '⚡',
};

// ========================================
// パックタブコンポーネント
// ========================================

const PackTab = ({
  playerData,
  allCards,
  cardValueMap,
  onPlayerDataUpdate,
  onOpenPack,
  onGoToCollection,
  isMobile,
  initialPackType = null, // パック開封後に復元する初期パックタイプ
  initialAttribute = null, // パック開封後に復元する初期属性
  accessToken = null, // アクセストークン（サーバーAPI用）
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedPackType, setSelectedPackType] = useState(initialPackType || 'standard');
  const [selectedAttribute, setSelectedAttribute] = useState(initialAttribute || '炎');

  const packInfo = packSystem.getPackInfo();
  const canBuy = currencyManager.canBuyPack(playerData);
  const bulkBuyOptions = packSystem.getBulkBuyOptions();

  // エラーメッセージを一定時間後にクリア
  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  // パック購入処理
  const handleBuyPack = async () => {
    if (!canBuy || isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.buyAndOpenPackAsync(playerData, allCards, cardValueMap, accessToken);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards);
        } else {
          showError(result.error || 'パック購入に失敗しました');
        }
      } else {
        const result = packSystem.buyAndOpenPack(playerData, allCards, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards);
        } else {
          showError(result.error || 'パック購入に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBuyPack error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 未開封パック開封処理
  const handleOpenUnopenedPack = async () => {
    if (!playerData.unopenedPacks || playerData.unopenedPacks <= 0 || isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.openUnopenedPackAsync(playerData, allCards, cardValueMap, accessToken);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards);
        } else {
          showError(result.error || '未開封パックの開封に失敗しました');
        }
      } else {
        const result = packSystem.openUnopenedPack(playerData, allCards, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards);
        } else {
          showError(result.error || '未開封パックの開封に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleOpenUnopenedPack error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // まとめ買い処理
  const handleBulkBuy = async (packCount) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.buyAndOpenMultiplePacksAsync(playerData, allCards, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount);
        } else {
          showError(result.error || 'まとめ買いに失敗しました');
        }
      } else {
        const result = packSystem.buyAndOpenMultiplePacks(playerData, allCards, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount);
        } else {
          showError(result.error || 'まとめ買いに失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBulkBuy error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // まとめ開封処理
  const handleBulkOpen = async (packCount) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.openMultipleUnopenedPacksAsync(playerData, allCards, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount);
        } else {
          showError(result.error || 'まとめ開封に失敗しました');
        }
      } else {
        const result = packSystem.openMultipleUnopenedPacks(playerData, allCards, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount);
        } else {
          showError(result.error || 'まとめ開封に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBulkOpen error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // 属性パック購入処理
  const handleBuyAttributePack = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.buyAndOpenAttributePackAsync(playerData, allCards, selectedAttribute, cardValueMap, accessToken);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, 1, { type: 'attribute', attribute: selectedAttribute });
        } else {
          showError(result.error || '属性パック購入に失敗しました');
        }
      } else {
        const result = packSystem.buyAndOpenAttributePack(playerData, allCards, selectedAttribute, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, 1, { type: 'attribute', attribute: selectedAttribute });
        } else {
          showError(result.error || '属性パック購入に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBuyAttributePack error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // ロイヤルパック購入処理
  const handleBuyRoyalPack = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.buyAndOpenRoyalPackAsync(playerData, allCards, cardValueMap, accessToken);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, 1, { type: 'royal' });
        } else {
          showError(result.error || 'ロイヤルパック購入に失敗しました');
        }
      } else {
        const result = packSystem.buyAndOpenRoyalPack(playerData, allCards, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, 1, { type: 'royal' });
        } else {
          showError(result.error || 'ロイヤルパック購入に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBuyRoyalPack error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // ロイヤルパックまとめ買い処理
  const handleBulkBuyRoyal = async (packCount) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      if (USE_SERVER_API) {
        const result = await packSystemServer.buyAndOpenMultipleRoyalPacksAsync(playerData, allCards, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount, { type: 'royal' });
        } else {
          showError(result.error || 'ロイヤルパックまとめ買いに失敗しました');
        }
      } else {
        const result = packSystem.buyAndOpenMultipleRoyalPacks(playerData, allCards, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount, { type: 'royal' });
        } else {
          showError(result.error || 'ロイヤルパックまとめ買いに失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBulkBuyRoyal error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // タイプ別未開封パック開封処理
  const handleOpenTypedUnopenedPack = async (packType) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      const getPackInfo = (type) => {
        if (type === 'royal') return { type: 'royal' };
        if (type === 'normal') return null;
        return { type: 'attribute', attribute: { fire: '炎', water: '水', light: '光', dark: '闇', primitive: '原始', future: '未来' }[type] };
      };

      if (USE_SERVER_API) {
        const result = await packSystemServer.openTypedUnopenedPackAsync(playerData, allCards, packType, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, 1, getPackInfo(packType));
        } else {
          showError(result.error || '未開封パックの開封に失敗しました');
        }
      } else {
        const result = packSystem.openTypedUnopenedPack(playerData, allCards, packType, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, 1, getPackInfo(packType));
        } else {
          showError(result.error || '未開封パックの開封に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleOpenTypedUnopenedPack error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // タイプ別未開封パックまとめ開封処理
  const handleBulkOpenTyped = async (packType, packCount) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      const getPackInfo = (type) => {
        if (type === 'royal') return { type: 'royal' };
        if (type === 'normal') return null;
        return { type: 'attribute', attribute: { fire: '炎', water: '水', light: '光', dark: '闘', primitive: '原始', future: '未来' }[type] };
      };

      if (USE_SERVER_API) {
        const result = await packSystemServer.openMultipleTypedUnopenedPacksAsync(playerData, allCards, packType, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount, getPackInfo(packType));
        } else {
          showError(result.error || 'まとめ開封に失敗しました');
        }
      } else {
        const result = packSystem.openMultipleTypedUnopenedPacks(playerData, allCards, packType, packCount, cardValueMap);
        if (result.success) {
          onPlayerDataUpdate(result.playerData);
          onOpenPack(result.cards, result.packCount, getPackInfo(packType));
        } else {
          showError(result.error || 'まとめ開封に失敗しました');
        }
      }
    } catch (err) {
      console.error('[PackTab] handleBulkOpenTyped error:', err);
      showError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsProcessing(false);
    }
  };

  // タイプ別未開封パック数を取得
  const typedPackCounts = packSystem.getTypedUnopenedPackCounts(playerData);
  const hasTypedPacks = Object.entries(typedPackCounts).some(([type, count]) => type !== 'normal' && count > 0);

  return (
    <div style={{
      ...styles.packTabContent,
      flexDirection: isMobile ? 'column' : 'row',
      padding: isMobile ? '16px 12px' : '24px',
      gap: isMobile ? '16px' : '24px',
      alignItems: isMobile ? 'stretch' : 'flex-start',
    }}>
      {/* エラーメッセージ */}
      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(220, 53, 69, 0.95)',
          color: '#fff',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          maxWidth: '90%',
          textAlign: 'center',
          fontSize: '14px',
        }}>
          ⚠️ {errorMessage}
        </div>
      )}
      {/* 右カラム - パック購入（モバイルでは上に表示） */}
      <div style={{
        ...styles.packRightColumn,
        maxWidth: isMobile ? '100%' : '400px',
        width: '100%',
        order: isMobile ? -1 : 0,
      }}>
        {/* 未開封パックセクション */}
        {playerData.unopenedPacks > 0 && (
          <div style={styles.unopenedPackCard}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffd700' }}>🎁 報酬パック</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#fff' }}>
              {playerData.unopenedPacks}個
            </div>
            <button
              style={styles.openPackButton}
              onClick={handleOpenUnopenedPack}
              disabled={isProcessing}
            >
              {isProcessing ? '開封中...' : '🎴 1個開ける'}
            </button>
            {playerData.unopenedPacks >= 3 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
                {[3, 5, 10].filter(n => playerData.unopenedPacks >= n).map((count) => (
                  <button
                    key={count}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,215,0,0.5)',
                      background: 'rgba(255,215,0,0.15)',
                      color: '#ffd700',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                    onClick={() => handleBulkOpen(count)}
                    disabled={isProcessing}
                  >
                    {count}個まとめ
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 属性・ロイヤル報酬パックセクション */}
        {hasTypedPacks && (
          <div style={{
            ...styles.unopenedPackCard,
            background: 'linear-gradient(135deg, rgba(107,76,230,0.2) 0%, rgba(157,76,230,0.2) 100%)',
            borderColor: '#9d4ce6',
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e6b4ff' }}>🎁 特別報酬パック</div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              justifyContent: 'center',
              marginTop: '8px',
            }}>
              {Object.entries(typedPackCounts)
                .filter(([type, count]) => type !== 'normal' && count > 0)
                .map(([packType, count]) => (
                  <div key={packType} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: packType === 'royal'
                      ? 'linear-gradient(135deg, rgba(255,215,0,0.3) 0%, rgba(255,149,0,0.3) 100%)'
                      : 'rgba(107,76,230,0.2)',
                    border: packType === 'royal' ? '1px solid #ffd700' : '1px solid #6b4ce6',
                    minWidth: '80px',
                  }}>
                    <div style={{ fontSize: '24px' }}>{packSystem.getPackTypeIcon(packType)}</div>
                    <div style={{
                      fontSize: '11px',
                      color: packType === 'royal' ? '#ffd700' : '#e0e0e0',
                      fontWeight: 'bold',
                    }}>
                      {packSystem.getPackTypeName(packType)}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#fff',
                      margin: '4px 0',
                    }}>
                      {count}個
                    </div>
                    <button
                      style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        border: 'none',
                        background: packType === 'royal'
                          ? 'linear-gradient(135deg, #ffd700 0%, #ff9500 100%)'
                          : 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
                        color: packType === 'royal' ? '#1a1a2e' : '#fff',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                      }}
                      onClick={() => handleOpenTypedUnopenedPack(packType)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? '...' : '開封'}
                    </button>
                    {count >= 3 && (
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                        {[3, 5].filter(n => count >= n).map((num) => (
                          <button
                            key={num}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              border: packType === 'royal' ? '1px solid rgba(255,215,0,0.5)' : '1px solid rgba(107,76,230,0.5)',
                              background: 'transparent',
                              color: packType === 'royal' ? '#ffd700' : '#9d4ce6',
                              cursor: 'pointer',
                              fontSize: '10px',
                            }}
                            onClick={() => handleBulkOpenTyped(packType, num)}
                            disabled={isProcessing}
                          >
                            {num}個
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* パック購入セクション */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%' }}>
          <div style={{ ...styles.sectionTitle, marginBottom: 0 }}>パック購入</div>

          {/* パックタイプ選択タブ */}
          <div style={styles.packTypeTabs}>
            <button
              style={{ ...styles.packTypeTab, ...(selectedPackType === 'standard' ? styles.packTypeTabActive : {}) }}
              onClick={() => setSelectedPackType('standard')}
            >
              通常
            </button>
            <button
              style={{ ...styles.packTypeTab, ...(selectedPackType === 'attribute' ? styles.packTypeTabActive : {}) }}
              onClick={() => setSelectedPackType('attribute')}
            >
              属性別
            </button>
            <button
              style={{
                ...styles.packTypeTab,
                ...(selectedPackType === 'royal' ? { background: 'linear-gradient(135deg, #ffd700 0%, #ff9500 100%)', color: '#1a1a2e' } : {}),
              }}
              onClick={() => setSelectedPackType('royal')}
            >
              👑 ロイヤル
            </button>
          </div>

          {/* スタンダードパック */}
          {selectedPackType === 'standard' && (
            <div style={styles.packCard}>
              <div style={styles.packName}>スタンダードパック</div>
              <div style={styles.packImage}>🎴</div>
              <div style={styles.packInfo}>{packInfo.cardsPerPack}枚入り</div>
              <div style={styles.packPrice}>{packInfo.priceFormatted}</div>
              <button
                style={{ ...styles.buyButton, ...(!canBuy || isProcessing ? styles.buyButtonDisabled : {}) }}
                onClick={handleBuyPack}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                disabled={!canBuy || isProcessing}
              >
                {isProcessing ? '処理中...' : canBuy ? '購入する' : 'G不足'}
              </button>
              {!canBuy && (
                <div style={{ fontSize: '12px', color: '#ff6b6b' }}>
                  あと {currencyManager.formatGold(ECONOMY.PACK_PRICE - playerData.gold)} 必要
                </div>
              )}
              {/* まとめ買いセクション */}
              <div style={styles.bulkBuySection}>
                <div style={styles.bulkBuyTitle}>📦 まとめ買い</div>
                <div style={styles.bulkBuyButtons}>
                  {bulkBuyOptions.map((option) => {
                    const canAfford = currencyManager.canAfford(playerData, option.totalPrice);
                    return (
                      <button
                        key={option.count}
                        style={{
                          ...styles.bulkBuyButton,
                          ...((!canAfford || isProcessing) ? styles.bulkBuyButtonDisabled : {}),
                        }}
                        onClick={() => handleBulkBuy(option.count)}
                        disabled={!canAfford || isProcessing}
                      >
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{option.count}パック</span>
                        <span style={{ fontSize: '11px', color: canAfford ? '#ffd700' : '#606060' }}>
                          {currencyManager.formatGold(option.totalPrice)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 属性別パック */}
          {selectedPackType === 'attribute' && (
            <div style={styles.packCard}>
              <div style={styles.packName}>{selectedAttribute}属性パック</div>
              <div style={styles.attributeGrid}>
                {ATTRIBUTE_PACK.ATTRIBUTES.map((attr) => (
                  <button
                    key={attr}
                    style={{
                      ...styles.attributeButton,
                      ...(selectedAttribute === attr ? styles.attributeButtonSelected : {}),
                    }}
                    onClick={() => setSelectedAttribute(attr)}
                  >
                    <span style={{ fontSize: '24px' }}>{ATTRIBUTE_ICONS[attr]}</span>
                    <span style={{ fontSize: '12px' }}>{attr}</span>
                  </button>
                ))}
              </div>
              <div style={{ ...styles.packImage, background: 'linear-gradient(135deg, #4a4a6a 0%, #6a6a8a 100%)' }}>
                <span>{ATTRIBUTE_ICONS[selectedAttribute]}</span>
              </div>
              <div style={styles.packInfo}>{ATTRIBUTE_PACK.CARDS_PER_PACK}枚入り（{selectedAttribute}属性のみ）</div>
              <div style={styles.packPrice}>{currencyManager.formatGold(ATTRIBUTE_PACK.SHOP_PRICE)}</div>
              <button
                style={{
                  ...styles.buyButton,
                  ...(playerData.gold < ATTRIBUTE_PACK.SHOP_PRICE || isProcessing ? styles.buyButtonDisabled : {}),
                }}
                onClick={handleBuyAttributePack}
                disabled={playerData.gold < ATTRIBUTE_PACK.SHOP_PRICE || isProcessing}
              >
                {isProcessing ? '処理中...' : playerData.gold >= ATTRIBUTE_PACK.SHOP_PRICE ? '購入する' : 'G不足'}
              </button>
            </div>
          )}

          {/* ロイヤルパック */}
          {selectedPackType === 'royal' && (
            <div style={styles.royalPackCard}>
              <div style={{ ...styles.packName, color: '#ffd700' }}>👑 ロイヤルパック</div>
              <div style={styles.royalPackImage}>💎</div>
              <div style={{ fontSize: '12px', color: '#c0a060', textAlign: 'center', lineHeight: '1.6' }}>
                枠1-2: SR以上確定<br />
                枠3-4: UR以上確定<br />
                枠5: HR以上確定
              </div>
              <div style={styles.packInfo}>{ROYAL_PACK.CARDS_PER_PACK}枚入り（全属性）</div>
              <div style={{ ...styles.packPrice, fontSize: '28px' }}>{currencyManager.formatGold(ROYAL_PACK.PRICE)}</div>
              <button
                style={{
                  ...styles.openPackButton,
                  ...(playerData.gold < ROYAL_PACK.PRICE || isProcessing ? { background: 'rgba(100,100,100,0.5)', cursor: 'not-allowed' } : {}),
                }}
                onClick={handleBuyRoyalPack}
                disabled={playerData.gold < ROYAL_PACK.PRICE || isProcessing}
              >
                {isProcessing ? '処理中...' : playerData.gold >= ROYAL_PACK.PRICE ? '購入する' : 'G不足'}
              </button>
              {/* ロイヤルパックまとめ買いセクション */}
              <div style={{
                ...styles.bulkBuySection,
                background: 'rgba(255,215,0,0.1)',
                borderColor: 'rgba(255,215,0,0.4)',
              }}>
                <div style={{ ...styles.bulkBuyTitle, color: '#ffd700' }}>👑 まとめ買い</div>
                <div style={styles.bulkBuyButtons}>
                  {packSystem.getRoyalBulkBuyOptions().map((option) => {
                    const canAfford = currencyManager.canAfford(playerData, option.totalPrice);
                    return (
                      <button
                        key={option.count}
                        style={{
                          ...styles.bulkBuyButton,
                          borderColor: canAfford ? 'rgba(255,215,0,0.5)' : 'rgba(100,100,100,0.3)',
                          background: canAfford ? 'rgba(255,215,0,0.15)' : 'rgba(50,50,50,0.5)',
                          color: canAfford ? '#ffd700' : '#606060',
                          ...((!canAfford || isProcessing) ? { cursor: 'not-allowed' } : {}),
                        }}
                        onClick={() => handleBulkBuyRoyal(option.count)}
                        disabled={!canAfford || isProcessing}
                      >
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{option.count}パック</span>
                        <span style={{ fontSize: '11px' }}>
                          {currencyManager.formatGold(option.totalPrice)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#808080', textAlign: 'center' }}>
                期待値: 約120,000G / 元取れる確率: 約20%
              </div>
            </div>
          )}

          {/* カード売却リンク */}
          <div style={{
            padding: '16px',
            background: 'rgba(30,30,50,0.5)',
            borderRadius: '12px',
            border: '1px solid rgba(107,76,230,0.3)',
            width: '100%',
            boxSizing: 'border-box',
            textAlign: 'center',
          }}>
            <button
              style={{
                padding: '12px 32px',
                borderRadius: '8px',
                border: '1px solid rgba(107,76,230,0.5)',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                background: 'rgba(30,30,50,0.8)',
                color: '#e0e0e0',
              }}
              onClick={onGoToCollection}
            >
              📚 コレクションから売却 →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========================================
// 市場タブコンポーネント
// ========================================

const MarketTab = ({ playerData, onOpenMarketAnalysis, isMobile }) => {
  const market = playerData?.market;
  const msiData = getMarketIndexChartData(market?.priceHistory);
  const sparklineData = generateSparklineData(market?.priceHistory?.marketIndex || []);

  const getConditionStyle = (condition) => {
    if (condition === '好調' || condition === '上昇') return { bg: 'rgba(76,175,80,0.2)', color: '#4caf50' };
    if (condition === '不調' || condition === '下落') return { bg: 'rgba(244,67,54,0.2)', color: '#f44336' };
    return { bg: 'rgba(128,128,128,0.2)', color: '#888' };
  };

  if (!market) {
    return (
      <div style={{ ...styles.marketTabContent, padding: isMobile ? '16px 12px' : '24px' }}>
        <div style={{ color: '#888', textAlign: 'center' }}>市場データがありません</div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.marketTabContent, padding: isMobile ? '16px 12px' : '24px' }}>
      {/* MSIセクション */}
      {msiData && msiData.currentPrice > 0 && (
        <div style={styles.msiSection}>
          <div style={styles.msiHeader}>
            <div style={styles.msiTitle}>
              <span style={styles.msiIcon}>📊</span>
              <span style={styles.msiLabel}>Magic Spirit Index (MSI)</span>
            </div>
            {onOpenMarketAnalysis && (
              <button
                style={styles.msiDetailButton}
                onClick={onOpenMarketAnalysis}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,215,0,0.3)';
                  e.target.style.borderColor = 'rgba(255,215,0,0.8)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,215,0,0.1)';
                  e.target.style.borderColor = 'rgba(255,215,0,0.5)';
                }}
              >
                詳細分析 →
              </button>
            )}
          </div>
          <div style={styles.msiBody}>
            <div style={styles.msiValue}>{msiData.currentPrice.toLocaleString()}</div>
            <div style={styles.msiChange}>
              <span style={{ ...styles.msiChangeValue, color: getTrendColor(msiData.changePercent) }}>
                {getTrendIcon(msiData.changePercent)} {msiData.changePercent > 0 ? '+' : ''}{msiData.changePercent}%
              </span>
              <span style={{
                ...styles.msiCondition,
                backgroundColor: getConditionStyle(msiData.marketCondition).bg,
                color: getConditionStyle(msiData.marketCondition).color,
              }}>
                {msiData.marketCondition}
              </span>
            </div>
            {sparklineData.length > 0 && (
              <div style={styles.msiSparkline}>
                {sparklineData.map((value, index) => (
                  <div key={index} style={{ ...styles.msiSparklineBar, height: `${Math.max(value, 5)}%` }} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* マーケットニュースパネル */}
      <div style={styles.marketNewsPanel}>
        <div style={styles.marketNewsHeader}>
          <span style={styles.marketNewsIcon}>📰</span>
          <span style={styles.marketNewsTitle}>マーケットニュース</span>
          <span style={styles.marketDayCounter}>Day {market.currentDay + 1}</span>
        </div>

        {/* 週間トレンド (サーバーデータ) */}
        {market.serverActiveTrend && (
          <div style={styles.trendSection}>
            <div style={styles.trendLabel}>📅 週間トレンド</div>
            <div style={styles.trendName}>{market.serverActiveTrend.name}</div>
            <div style={styles.trendEffects}>
              {market.serverActiveTrend.effects?.map((effect, i) => {
                const target = effect?.target || {};
                const targetText = target.attribute ? `${target.attribute}属性`
                  : target.all ? '全体'
                  : target.maxCost !== undefined ? `コスト${target.maxCost}以下`
                  : target.minCost !== undefined ? `コスト${target.minCost}以上`
                  : target.keyword ? `${target.keyword}`
                  : target.tiers ? `${target.tiers.join('/')}ティア`
                  : target.minRarity ? `${target.minRarity}以上`
                  : '対象';
                const modifierStyle = effect.modifier > 0 ? styles.trendEffectUp : effect.modifier < 0 ? styles.trendEffectDown : {};
                return (
                  <span key={i} style={{ marginRight: '12px', ...modifierStyle }}>
                    {targetText} {effect.modifier > 0 ? '+' : ''}{effect.modifier}%
                  </span>
                );
              })}
            </div>
            {market.serverActiveTrend.description && (
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                {market.serverActiveTrend.description}
              </div>
            )}
          </div>
        )}

        {/* サーバーニュース（本日のニュース） */}
        {market.serverNews && market.serverNews.length > 0 && (
          <div style={styles.newsSection}>
            <div style={styles.newsLabel}>📰 本日のニュース</div>
            <div style={styles.newsText}>「{market.serverNews[0].text}」</div>
            {market.serverNews[0].modifier !== undefined && market.serverNews[0].modifier !== 0 && (
              <div style={{
                ...styles.newsModifier,
                ...(market.serverNews[0].modifier > 0 ? styles.modifierUp : styles.modifierDown),
              }}>
                → [{market.serverNews[0].target || '全体'}]
                {' '}{market.serverNews[0].modifier > 0 ? '+' : ''}{market.serverNews[0].modifier}%
              </div>
            )}
          </div>
        )}

        {/* 連鎖効果 (サーバーデータ) */}
        {market.serverChainNews && market.serverChainNews.length > 0 && (
          <div style={styles.chainSection}>
            <div style={styles.chainLabel}><span>🔗</span><span>連鎖効果</span></div>
            {market.serverChainNews.map((chain, i) => {
              const isLast = i === market.serverChainNews.length - 1;
              const typeLabel = chain.chainType === 'positive' ? '正連鎖'
                : chain.chainType === 'counter' ? '対抗連鎖'
                : chain.chainType === 'negative' ? '負連鎖'
                : chain.chainType === 'rivalry' ? '宿敵連鎖'
                : chain.chainType === 'dominion' ? '覇権連鎖'
                : chain.chainType === 'triangle' ? '三つ巴連鎖'
                : chain.chainType === 'technology' ? '技術連鎖'
                : chain.chainType === 'ecosystem' ? '生態系連鎖'
                : '連鎖';
              const typeStyle = chain.chainType === 'positive' ? styles.chainTypePositive
                : chain.chainType === 'counter' ? styles.chainTypeCounter
                : chain.chainType === 'negative' ? styles.chainTypeNegative
                : styles.chainTypeSpecial;
              const targetCondition = chain.targetCondition || {};
              const targetText = targetCondition.category ? `[${targetCondition.category}]`
                : targetCondition.attribute ? `[${targetCondition.attribute}属性]`
                : chain.target ? `[${chain.target}]`
                : '[連鎖対象]';
              return (
                <div key={i} style={{ ...styles.chainItem, ...(isLast ? styles.chainItemLast : {}) }}>
                  <span style={{ ...styles.chainType, ...typeStyle }}>[{typeLabel}]</span>
                  <span>{chain.text}</span>
                  <div style={{ ...styles.chainModifier, ...(chain.modifier > 0 ? styles.modifierUp : styles.modifierDown) }}>
                    → {targetText} {chain.modifier > 0 ? '+' : ''}{chain.modifier}%
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* サーバーニュース（サブニュース - 2件目以降） */}
        {market.serverNews && market.serverNews.length > 1 && (
          <div style={styles.subNewsSection}>
            <div style={styles.subNewsLabel}><span>📰</span><span>サブニュース</span></div>
            <div style={styles.subNewsText}>「{market.serverNews[1].text}」</div>
            {market.serverNews[1].modifier !== undefined && market.serverNews[1].modifier !== 0 && (
              <div style={{ ...styles.subNewsModifier, ...(market.serverNews[1].modifier > 0 ? styles.modifierUp : styles.modifierDown) }}>
                → [{market.serverNews[1].target || '全体'}]
                {' '}{market.serverNews[1].modifier > 0 ? '+' : ''}{market.serverNews[1].modifier}%
              </div>
            )}
          </div>
        )}

        {/* 突発イベント (サーバーデータ) */}
        {market.serverSuddenEvent && (
          <div style={styles.suddenEventSection}>
            <div style={styles.suddenEventLabel}><span>⚡</span><span>突発イベント発生中！</span></div>
            <div style={styles.suddenEventName}>{market.serverSuddenEvent.text}</div>
            <div style={styles.suddenEventEffects}>
              <div style={styles.suddenEventEffect}>
                <span>{market.serverSuddenEvent.target}: </span>
                <span style={market.serverSuddenEvent.modifier > 0 ? styles.modifierUp : styles.modifierDown}>
                  {market.serverSuddenEvent.modifier > 0 ? '+' : ''}{market.serverSuddenEvent.modifier}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* スポットライトイベント (サーバーデータ) */}
        {market.serverSpotlightEvent && (
          <div style={styles.spotlightSection}>
            <div style={styles.spotlightLabel}><span>🌟</span><span>スポットライトイベント！</span></div>
            <div style={styles.spotlightTitle}>{market.serverSpotlightEvent.title || '注目カード'}</div>
            <div style={styles.spotlightCardName}>{market.serverSpotlightEvent.cardName}</div>
            <div style={styles.spotlightDescription}>{market.serverSpotlightEvent.description}</div>
            <div style={styles.spotlightBonus}>売却価格 10倍！</div>
            <div style={styles.spotlightHint}>※ 今日限定のチャンスです</div>
          </div>
        )}

        {/* 夜間変動レポート (サーバーデータ) */}
        {market.serverOvernightVolatility && (
          <div style={{
            ...styles.overnightSection,
            ...(market.serverOvernightVolatility.direction === 'up' ? styles.overnightSectionUp : styles.overnightSectionDown),
          }}>
            <div style={{
              ...styles.overnightLabel,
              color: market.serverOvernightVolatility.direction === 'up' ? '#4caf50' : '#f44336',
            }}>
              <span>⚡</span>
              <span>[夜間変動レポート]</span>
            </div>
            <div style={styles.overnightText}>{market.serverOvernightVolatility.text}</div>
            <div style={{
              ...styles.overnightPercent,
              color: market.serverOvernightVolatility.direction === 'up' ? '#4caf50' : '#f44336',
            }}>
              {market.serverOvernightVolatility.spikePercent > 0 ? '+' : ''}{market.serverOvernightVolatility.spikePercent}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ========================================
// 好感度ハートアイコン
// ========================================

const FavorabilityHearts = ({ level }) => {
  const hearts = [];
  for (let i = 0; i < 5; i++) {
    hearts.push(
      <span key={i} style={{ opacity: i < level ? 1 : 0.3 }}>
        ♥
      </span>
    );
  }
  return <div style={styles.merchantFav}>{hearts}</div>;
};

// ========================================
// 商人カードコンポーネント
// ========================================

const MerchantCard = ({
  merchant,
  favorability,
  hasWishlistItem,
  available,
  onClick,
  isMobile = false,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const favLevel = favorability?.level || 1;

  const cardStyle = {
    ...styles.merchantCard,
    ...(isHovered && available ? styles.merchantCardHover : {}),
    ...(!available ? styles.merchantCardDisabled : {}),
    padding: isMobile ? '12px' : '16px',
    gap: isMobile ? '12px' : '16px',
  };

  return (
    <div
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => available && onClick(merchant)}
    >
      <div style={{
        ...styles.merchantIcon,
        fontSize: isMobile ? '28px' : '36px',
        width: isMobile ? '40px' : '50px',
        height: isMobile ? '40px' : '50px',
      }}>{merchant.icon}</div>
      <div style={styles.merchantInfo}>
        <div style={{
          ...styles.merchantName,
          fontSize: isMobile ? '14px' : '16px',
        }}>
          {merchant.name}
          {hasWishlistItem && <span style={styles.wishlistBadge}>⭐</span>}
        </div>
        <div style={{
          ...styles.merchantSpecialty,
          fontSize: isMobile ? '11px' : '12px',
        }}>
          {merchant.specialty
            ? merchant.specialty.type === 'attribute'
              ? `${merchant.specialty.value}属性`
              : merchant.specialty.value.join(' / ')
            : '全属性'}
        </div>
        <FavorabilityHearts level={favLevel} />
      </div>
      {available && (
        <button style={{
          ...styles.enterButton,
          padding: isMobile ? '6px 12px' : '8px 16px',
          fontSize: isMobile ? '12px' : '14px',
        }}>入店</button>
      )}
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

const MerchantGuild = ({
  playerData,
  allCards = [],
  cardValueMap = {},
  onBack,
  onEnterShop,
  onPlayerDataUpdate,
  onPlaceBet,
  onCancelBet,
  onPurchaseInfo,
  onClaimTournamentReward,
  onOpenPack,
  onGoToCollection,
  onOpenMarketAnalysis,
  initialTab = 'merchant', // 'pack' | 'merchant' | 'tournament' | 'investment' | 'market'
  packTabInitialState = null, // { packType, attribute } パックタブの初期選択状態
}) => {
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const accessToken = session?.access_token;
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);

  // 初回マウント時のinitialTabを記録（再マウント時のみ反映）
  const initialTabRef = useRef(initialTab);
  const isFirstMount = useRef(true);

  // initialTabが変更されたときにactiveTabを更新（初回マウント後は無効化）
  // これにより、ユーザーが別タブを見ている時に突然タブが切り替わる問題を防ぐ
  useEffect(() => {
    // 初回マウント時はuseStateで初期化済みなので何もしない
    if (isFirstMount.current) {
      isFirstMount.current = false;
      initialTabRef.current = initialTab;
      return;
    }
    // initialTabが実際に変更された場合のみ更新（同じ値での再レンダリングを無視）
    if (initialTab && initialTab !== initialTabRef.current) {
      initialTabRef.current = initialTab;
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // スクロール中のタップ誤検知を防ぐための状態
  const touchStartY = useRef(null);
  const lastTabSwitchTime = useRef(0);

  // タブ切り替えハンドラー（デバウンス付き）
  const handleTabSwitch = useCallback((tab) => {
    const now = Date.now();
    // 300ms以内の連続タップを無視（スクロール中の誤タップ対策）
    if (now - lastTabSwitchTime.current < 300) {
      return;
    }
    lastTabSwitchTime.current = now;
    setActiveTab(tab);
  }, []);

  // タッチ開始位置を記録
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  // タッチ終了時にスクロールかタップかを判定
  const handleTouchEnd = useCallback((tab, e) => {
    if (touchStartY.current === null) return;
    const touchEndY = e.changedTouches[0].clientY;
    const distance = Math.abs(touchEndY - touchStartY.current);
    touchStartY.current = null;
    // 移動距離が15px未満ならタップとして認識
    if (distance < 15) {
      handleTabSwitch(tab);
    }
  }, [handleTabSwitch]);

  const online = !!economyAccount();
  const dayId = online ? playerData?.merchantData?.todayAppearances?.dayId ?? 0 : playerData?.market?.currentDay || 0;
  useEffect(() => {
    if (!online) return;
    economyRequest('merchant/status').catch(error => alert(error.message));
    economyRequest('tournament/current').catch(error => alert(error.message));
  }, [online]);
  const merchantData = playerData?.merchantData || {};
  const favorability = merchantData.favorability || {};
  const tickets = merchantData.tickets || { attribute: 0, dark: 0, traveler: 0 };
  const wishlist = merchantData.wishlist || [];
  const marketState = playerData?.market;

  // 禁忌カード数を計算（コンポーネント内で計算）
  const forbiddenCount = useMemo(() => {
    return countForbiddenCards(playerData, allCards);
  }, [playerData, allCards]);

  // マーケットインデックスを構築（FXレート計算用・総資産計算用）
  const marketIndex = useMemo(() => {
    if (!allCards || allCards.length === 0) return null;
    return buildMarketIndex(allCards);
  }, [allCards]);

  // 総資産を計算（コンポーネント内で計算、ファンド・FX含む）
  const totalAssets = useMemo(() => {
    const assets = calculateTotalAssets(playerData, allCards, marketState, marketIndex);
    return assets.totalMarket;
  }, [playerData, allCards, marketState, marketIndex]);

  // 今日出現する商人を計算（キャッシュ付き）
  const { appearances, updated, newMerchantData } = useMemo(() => {
    if (online) return { appearances: merchantData.todayAppearances || {}, updated: false, newMerchantData: null };
    return getTodayAppearances(merchantData, dayId, {
      forbiddenCount,
      totalAssets,
    });
  }, [merchantData, dayId, forbiddenCount, totalAssets, online]);

  // merchantDataが更新された場合、親に通知
  useEffect(() => {
    if (updated && newMerchantData && onPlayerDataUpdate) {
      onPlayerDataUpdate({
        ...playerData,
        merchantData: newMerchantData,
      });
    }
  }, [updated, newMerchantData, onPlayerDataUpdate, playerData]);

  // 出現商人を使いやすい形式に変換
  const availableMerchants = useMemo(() => ({
    general: ['マルクス'],
    attribute: appearances.attribute || [],
    collector: appearances.collector || [],
    dark: appearances.dark ? ['名無し'] : [],
    traveler: appearances.traveler ? ['ゼルヴァン'] : [],
  }), [appearances]);

  // ウィッシュリストに含まれるカードを持つ商人をチェック
  // ※品揃えは日付更新時に生成済み（processDayAdvancement）
  const merchantsWithWishlist = useMemo(() => {
    if (!wishlist || wishlist.length === 0) return new Set();

    const stocks = merchantData.todayStock?.stocks || {};
    const result = new Set();

    for (const [merchantName, stock] of Object.entries(stocks)) {
      if (stock && stock.some(item => wishlist.includes(item.cardId))) {
        result.add(merchantName);
      }
    }

    return result;
  }, [wishlist, merchantData.todayStock]);

  const handleEnterShop = (merchant) => {
    if (onEnterShop) {
      onEnterShop(merchant.name);
    }
  };

  // 属性商人呼び出しチケット使用
  const handleCallAttributeMerchant = async (merchantName) => {
    if (online) {
      try { await economyRequest('merchant/ticket/use', { merchant_name: merchantName }); setShowTicketModal(false); }
      catch (error) { alert(error.message); }
      return;
    }
    const result = callAttributeMerchant(merchantData, merchantName, dayId);
    if (result.success && onPlayerDataUpdate) {
      onPlayerDataUpdate({
        ...playerData,
        merchantData: result.newMerchantData,
      });
    }
    setShowTicketModal(false);
    if (!result.success) {
      alert(result.message);
    }
  };

  // チケットで呼び出し可能な属性商人（今日出現していない商人）
  const callableMerchants = useMemo(() => {
    const currentAppearances = appearances.attribute || [];
    return ATTRIBUTE_MERCHANTS.filter(m => !currentAppearances.includes(m.name));
  }, [appearances.attribute]);

  // 曜日名
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const currentWeekday = weekdays[(dayId + (online ? 4 : 0)) % 7];

  return (
    <div style={isMobile ? styles.containerMobile : styles.container}>
      {/* ヘッダー */}
      <div style={isMobile ? styles.headerMobile : {
        ...styles.header,
        padding: '12px 24px',
      }}>
        <div style={styles.headerLeft}>
          <button
            style={{
              ...styles.backButton,
              padding: isMobile ? '6px 12px' : '8px 16px',
              fontSize: isMobile ? '12px' : '14px',
            }}
            onClick={onBack}
            onMouseEnter={(e) => e.target.style.background = 'rgba(107,76,230,0.5)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(107,76,230,0.3)'}
          >
            ← 戻る
          </button>
          <span style={{ ...styles.title, fontSize: isMobile ? '16px' : '24px' }}>🏪 商人ギルド</span>
        </div>
        <div style={{ ...styles.goldDisplay, fontSize: isMobile ? '14px' : '18px' }}>
          💰 {(playerData?.gold || 0).toLocaleString()} G
        </div>
      </div>

      {/* タブナビゲーション（PC用） */}
      {!isMobile && (
      <div style={{
        ...styles.tabNavigation,
        padding: '8px 24px',
      }}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'pack' ? styles.tabActive : {}),
            padding: '10px 24px',
            fontSize: '14px',
          }}
          onClick={() => setActiveTab('pack')}
        >
          🎴 パック
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'merchant' ? styles.tabActive : {}),
            padding: '10px 24px',
            fontSize: '14px',
          }}
          onClick={() => setActiveTab('merchant')}
        >
          🛒 商人
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'tournament' ? styles.tabActive : {}),
            padding: '10px 24px',
            fontSize: '14px',
          }}
          onClick={() => setActiveTab('tournament')}
        >
          🏆 大会
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'investment' ? styles.tabActive : {}),
            padding: '10px 24px',
            fontSize: '14px',
          }}
          onClick={() => setActiveTab('investment')}
        >
          📊 投資
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'market' ? styles.tabActive : {}),
            padding: '10px 24px',
            fontSize: '14px',
          }}
          onClick={() => setActiveTab('market')}
        >
          📈 市場
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'trade' ? styles.tabActive : {}),
            padding: '10px 24px',
            fontSize: '14px',
          }}
          onClick={() => setActiveTab('trade')}
        >
          🔄 トレード
        </button>
      </div>
      )}

      {/* メインコンテンツ */}
      {activeTab === 'pack' ? (
        <PackTab
          playerData={playerData}
          allCards={allCards}
          cardValueMap={cardValueMap}
          onPlayerDataUpdate={onPlayerDataUpdate}
          onOpenPack={onOpenPack}
          onGoToCollection={onGoToCollection}
          isMobile={isMobile}
          initialPackType={packTabInitialState?.packType}
          initialAttribute={packTabInitialState?.attribute}
          accessToken={accessToken}
        />
      ) : activeTab === 'tournament' ? (
        <TournamentTab
          playerData={playerData}
          currentBattle={online ? playerData.economyBattleNumber : dayId}
          onPlaceBet={onPlaceBet}
          onCancelBet={onCancelBet}
          onPurchaseInfo={onPurchaseInfo}
          onClaimReward={onClaimTournamentReward}
        />
      ) : activeTab === 'investment' ? (
        <InformationBureau
          playerData={playerData}
          updatePlayerData={onPlayerDataUpdate}
          currentBattleNumber={online ? playerData.economyBattleNumber : dayId}
          allCards={allCards}
          marketIndex={marketIndex}
          marketState={marketState}
          onClose={() => setActiveTab('main')}
        />
      ) : activeTab === 'market' ? (
        <MarketTab
          playerData={playerData}
          onOpenMarketAnalysis={onOpenMarketAnalysis}
          isMobile={isMobile}
        />
      ) : activeTab === 'trade' ? (
        <TradeScreen
          playerData={playerData}
          allCards={allCards}
          cardValueMap={cardValueMap}
          onBack={() => setActiveTab('merchant')}
          onPlayerDataUpdate={onPlayerDataUpdate}
        />
      ) : (
      <div style={{
        ...styles.mainContent,
        padding: isMobile ? '16px 12px' : '24px',
      }}>
        {/* 本日の曜日 */}
        <div style={styles.noticeCard}>
          <span style={styles.noticeIcon}>📅</span>
          <span style={styles.noticeText}>
            本日は <strong>{currentWeekday}曜日</strong> です（{online ? new Date(dayId * 86400000).toLocaleDateString('ja-JP', { timeZone: 'UTC' }) : `${dayId}日目`}）
          </span>
        </div>

        {/* 常設商人 */}
        <div style={{
          ...styles.section,
          marginBottom: isMobile ? '24px' : '32px',
        }}>
          <div style={{
            ...styles.sectionTitle,
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: isMobile ? '12px' : '16px',
          }}>常設</div>
          <div style={{
            ...styles.merchantGrid,
            gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(160px, 1fr))' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: isMobile ? '12px' : '16px',
          }}>
            {/* 一般商人 */}
            {MERCHANTS['マルクス'] && (
              <MerchantCard
                merchant={MERCHANTS['マルクス']}
                favorability={favorability['マルクス']}
                hasWishlistItem={merchantsWithWishlist.has('マルクス')}
                available={true}
                onClick={handleEnterShop}
                isMobile={isMobile}
              />
            )}
            {/* 今日の属性商人 */}
            {availableMerchants.attribute.map(name => (
              <MerchantCard
                key={name}
                merchant={MERCHANTS[name]}
                favorability={favorability[name]}
                hasWishlistItem={merchantsWithWishlist.has(name)}
                available={true}
                onClick={handleEnterShop}
                isMobile={isMobile}
              />
            ))}
          </div>
        </div>

        {/* 本日の来訪者 */}
        {(availableMerchants.collector.length > 0 ||
          availableMerchants.dark.length > 0 ||
          availableMerchants.traveler.length > 0) && (
          <div style={{
            ...styles.section,
            marginBottom: isMobile ? '24px' : '32px',
          }}>
            <div style={{
              ...styles.sectionTitle,
              fontSize: isMobile ? '16px' : '18px',
              marginBottom: isMobile ? '12px' : '16px',
            }}>本日の来訪者</div>
            <div style={{
              ...styles.merchantGrid,
              gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(160px, 1fr))' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: isMobile ? '12px' : '16px',
            }}>
              {/* コレクター */}
              {availableMerchants.collector.map(name => (
                <MerchantCard
                  key={name}
                  merchant={MERCHANTS[name]}
                  favorability={favorability[name]}
                  hasWishlistItem={merchantsWithWishlist.has(name)}
                  available={true}
                  onClick={handleEnterShop}
                  isMobile={isMobile}
                />
              ))}
              {/* 闇商人 */}
              {availableMerchants.dark.map(name => (
                <MerchantCard
                  key={name}
                  merchant={MERCHANTS[name]}
                  favorability={favorability[name]}
                  hasWishlistItem={merchantsWithWishlist.has(name)}
                  available={true}
                  onClick={handleEnterShop}
                  isMobile={isMobile}
                />
              ))}
              {/* 旅商人 */}
              {availableMerchants.traveler.map(name => (
                <MerchantCard
                  key={name}
                  merchant={MERCHANTS[name]}
                  favorability={favorability[name]}
                  hasWishlistItem={merchantsWithWishlist.has(name)}
                  available={true}
                  onClick={handleEnterShop}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>
        )}

        {/* 所持チケット */}
        <div style={{
          ...styles.ticketSection,
          padding: isMobile ? '12px' : '16px',
          marginTop: isMobile ? '16px' : '24px',
        }}>
          <div style={{
            ...styles.sectionTitle,
            fontSize: isMobile ? '16px' : '18px',
            marginBottom: isMobile ? '8px' : '16px',
          }}>所持チケット</div>
          <div style={{
            ...styles.ticketGrid,
            gap: isMobile ? '8px' : '12px',
            flexDirection: isMobile ? 'column' : 'row',
          }}>
            <div style={{
              ...styles.ticketItem,
              padding: isMobile ? '6px 12px' : '8px 16px',
              fontSize: isMobile ? '12px' : '14px',
            }}>
              📜 {TICKETS.attribute.name}
              <span style={styles.ticketCount}>×{tickets.attribute}</span>
              {tickets.attribute > 0 && callableMerchants.length > 0 && (
                <button
                  style={{
                    ...styles.ticketUseButton,
                    fontSize: isMobile ? '11px' : '12px',
                  }}
                  onClick={() => setShowTicketModal(true)}
                >
                  使用
                </button>
              )}
            </div>
            <div style={{
              ...styles.ticketItem,
              padding: isMobile ? '6px 12px' : '8px 16px',
              fontSize: isMobile ? '12px' : '14px',
            }}>
              📜 {TICKETS.dark.name}
              <span style={styles.ticketCount}>×{tickets.dark}</span>
            </div>
            <div style={{
              ...styles.ticketItem,
              padding: isMobile ? '6px 12px' : '8px 16px',
              fontSize: isMobile ? '12px' : '14px',
            }}>
              📜 {TICKETS.traveler.name}
              <span style={styles.ticketCount}>×{tickets.traveler}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 属性商人呼び出しモーダル */}
      {showTicketModal && (
        <div style={styles.modal} onClick={() => setShowTicketModal(false)}>
          <div style={{
            ...styles.modalContent,
            padding: isMobile ? '16px' : '24px',
            maxWidth: isMobile ? '320px' : '400px',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              ...styles.modalTitle,
              fontSize: isMobile ? '16px' : '18px',
              marginBottom: isMobile ? '12px' : '16px',
            }}>呼び出す商人を選択</div>
            <div style={{
              ...styles.modalList,
              gap: isMobile ? '6px' : '8px',
              marginBottom: isMobile ? '12px' : '16px',
            }}>
              {callableMerchants.map(merchant => (
                <div
                  key={merchant.name}
                  style={{
                    ...styles.modalItem,
                    padding: isMobile ? '10px' : '12px',
                    gap: isMobile ? '10px' : '12px',
                  }}
                  onClick={() => handleCallAttributeMerchant(merchant.name)}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(107,76,230,0.4)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(107,76,230,0.2)'}
                >
                  <span style={{ fontSize: isMobile ? '20px' : '24px' }}>{merchant.icon}</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#fff', fontSize: isMobile ? '14px' : '16px' }}>{merchant.name}</div>
                    <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#a0a0a0' }}>
                      {merchant.specialty?.value}属性
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              style={{
                ...styles.modalCancelButton,
                padding: isMobile ? '10px' : '12px',
                fontSize: isMobile ? '13px' : '14px',
              }}
              onClick={() => setShowTicketModal(false)}
            >
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* フッタータブナビゲーション（モバイル用） */}
      {/* 投資タブ・トレードタブ選択時は非表示（独自のUIを持つため） */}
      {isMobile && activeTab !== 'investment' && activeTab !== 'trade' && (
        <div style={styles.footerTabNavigation}>
          <button
            style={{
              ...styles.footerTab,
              ...(activeTab === 'pack' ? styles.footerTabActive : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd('pack', e)}
            onClick={() => handleTabSwitch('pack')}
          >
            <span style={styles.footerTabIcon}>🎴</span>
            パック
          </button>
          <button
            style={{
              ...styles.footerTab,
              ...(activeTab === 'merchant' ? styles.footerTabActive : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd('merchant', e)}
            onClick={() => handleTabSwitch('merchant')}
          >
            <span style={styles.footerTabIcon}>🛒</span>
            商人
          </button>
          <button
            style={{
              ...styles.footerTab,
              ...(activeTab === 'tournament' ? styles.footerTabActive : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd('tournament', e)}
            onClick={() => handleTabSwitch('tournament')}
          >
            <span style={styles.footerTabIcon}>🏆</span>
            大会
          </button>
          <button
            style={{
              ...styles.footerTab,
              ...(activeTab === 'investment' ? styles.footerTabActive : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd('investment', e)}
            onClick={() => handleTabSwitch('investment')}
          >
            <span style={styles.footerTabIcon}>📊</span>
            投資
          </button>
          <button
            style={{
              ...styles.footerTab,
              ...(activeTab === 'market' ? styles.footerTabActive : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd('market', e)}
            onClick={() => handleTabSwitch('market')}
          >
            <span style={styles.footerTabIcon}>📈</span>
            市場
          </button>
          <button
            style={{
              ...styles.footerTab,
              ...(activeTab === 'trade' ? styles.footerTabActive : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleTouchEnd('trade', e)}
            onClick={() => handleTabSwitch('trade')}
          >
            <span style={styles.footerTabIcon}>🔄</span>
            交換
          </button>
        </div>
      )}
    </div>
  );
};

export default MerchantGuild;
